import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, ChangeDetectionStrategy } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Observable, debounceTime, distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';
import { ObjectAssociationType } from '../../../tokens-and-interfaces';
import { SimplifiedUser } from '../../../tokens-and-interfaces';
import { CrmAssociationFormField, FormFieldAssociation } from '../interface';
import { CrmAssociationFormFieldComponent } from '../association-form-field.component';

@Component({
  selector: 'crm-team-association-form-field',
  templateUrl: './team-association.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, CrmAssociationFormFieldComponent],
})
export class CrmTeamAssociationFormFieldComponent extends CrmAssociationFormField<ObjectAssociationType> {
  @Input({ required: true }) set associations(value: FormFieldAssociation[]) {
    this.setAssociations(value);
  }
  @Input() fixedId: string | null = null;
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input() placeholder = '';

  @Output() associationsChange = new EventEmitter<FormFieldAssociation[]>();

  protected readonly crmObjectType = 'User';
  protected readonly userService = this.config.services?.userService;
  protected readonly filteredAssociationsList = computed(() => {
    const associations = this.associationsList();
    return associations.filter((association) => association.crmObjectId !== this.fixedId);
  });

  protected override async fetchObjects(objectIds: string[]): Promise<FormFieldAssociation[]> {
    const users = this.userService ? await firstValueFrom(this.userService.getMultiUsers(objectIds)) : [];
    return this.mapAssociations(users);
  }

  protected override setupSearchTerm(): Observable<FormFieldAssociation[]> {
    return this.searchTerm$$.asObservable().pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search) => this.userService?.searchUsers?.(search) ?? of([])),
      map((filteredOptions) => this.mapAssociations(filteredOptions)),
      map((filteredOptions) => filteredOptions.filter((o) => o.crmObjectId !== this.fixedId)),
    );
  }

  private mapAssociations(users: SimplifiedUser[]): FormFieldAssociation[] {
    return (
      users?.map(
        (option) =>
          ({
            name: option.displayName,
            crmObjectId: option.userId,
            crmObjectType: this.crmObjectType,
          }) as FormFieldAssociation,
      ) || []
    );
  }

  handleAdd(association: FormFieldAssociation): void {
    const associations = this.associationsList();
    if (!associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      const newAssociations = [...associations, association];
      this.associationsList.set(newAssociations);
      this.associationsChange.emit(newAssociations);
    }
  }

  handleRemove(association: FormFieldAssociation): void {
    const associations = this.associationsList().filter((a) => a.crmObjectId !== association.crmObjectId);
    this.associationsList.set(associations);
    this.associationsChange.emit(associations);
  }

  handleSearchTermChange(searchTerm?: unknown): void {
    this.updateSearchTerm(searchTerm);
  }
}
